import { FlyoutListMenuProps, FlyoutListMenuWidget, MenuItemInfo } from '@tableau/widgets';
import React from 'react';

import { telemetryConsumerWrapper } from '../../../app/contexts/TelemetryConsumerWrapper';
import { ActionMenuItemClickedTelemetryContext } from '../../../app/contexts/TelemetryContexts';
import { ActionMenuItemClickedEventHandler } from '../../telemetryEvents/types/ContentTelemetryTypes';
import { ButtonControlledChildComponentProps } from './ButtonControlledComponent';

/**
 * These props are passed in by the component that instantiates the ButtonControlledComponent
 */
export interface ButtonControlledFlyoutListMenuOwnProps extends Pick<FlyoutListMenuProps, 'id' | 'role'> {
  readonly onComponentDidMount: () => void;
  readonly menuItems: MenuItemInfo[];
  readonly onMenuItemSelected: (menuItemValue: string) => void;
  readonly menuStyle?: React.CSSProperties;
}

export type ButtonControlledFlyoutListMenuProps =
  ButtonControlledChildComponentProps &
  ButtonControlledFlyoutListMenuOwnProps;

/**
 * This is a wrapper around the FlyoutListMenuWidget that is meant to be used as the child component
 * of a ButtonControlledComponent, so we can have a menu whose visibility is controlled by a button.
 *
 * To use it, create a wrapper component that takes ButtonControlledChildComponentProps and merges them with
 * ButtonControlledFlyoutListMenuOwnProps, then passes all those props to ButtonControlledFlyoutListMenu.
 *
 * This wrapper takes care of positioning the menu adjacent to the button and closing the menu
 * in the appropriate situations, so that the instantiating component needs to worry only about passing
 * in the menu and the callback handler.
 */
export class ButtonControlledFlyoutListMenu
  extends React.PureComponent<ButtonControlledFlyoutListMenuProps> {

  public componentDidMount() {
    if (this.props.onComponentDidMount) {
      this.props.onComponentDidMount();
    }
  }

  public render(): JSX.Element {
    return telemetryConsumerWrapper(
      ActionMenuItemClickedTelemetryContext.Consumer,
      this.renderFlyoutListMenu
    );
  }

  private getButtonClientRect = (): ClientRect => {
    const rect = this.props.anchorElement.getBoundingClientRect();
    return {
      bottom: rect.bottom - 1, // shift up 1 px to cover bottom border for line style dropdown buttons
      top: rect.top,
      width: rect.width,
      height: rect.height,
      right: rect.right,
      left: rect.left
    };
  }

  private renderFlyoutListMenu = (sendActionMenuItemClickTelemetry: ActionMenuItemClickedEventHandler) => {
    const defaultMenuStyle: React.CSSProperties = { fontWeight: 'normal', top: '-1px' };
    const menuStyle: React.CSSProperties = { ...defaultMenuStyle, ...this.props.menuStyle };

    return (
      <FlyoutListMenuWidget
        id={this.props.id}
        role={this.props.role}
        allowAutoPosition={true}
        menuStyle={menuStyle}
        getAnchorClientRect={this.getButtonClientRect}
        handleScrollOnScrollingRoot={this.props.closeMe}
        menuItemInfos={this.props.menuItems}
        onSelect={(menuItemValue: string) => {
          sendActionMenuItemClickTelemetry(menuItemValue);
          this.onMenuItemSelected(menuItemValue);
        }}
        requestCloseAll={this.props.closeMe}
        testId={this.props.testId}
        toggleEl={this.props.anchorElement}
        shouldReturnFocusToToggleEl={false} />
    );
  }

  private onMenuItemSelected = (menuItemValue: string): void => {
    this.props.closeMe();
    this.props.onMenuItemSelected(menuItemValue);
  }
}
